import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { format, isValid } from "date-fns";
import {
  ModalContent,
  ModalHeader,
  ErrorIcon,
  ModalBody,
  AlarmInfo,
  TroubleshootingSection,
  TroubleshootingList,
  ModalActions,
} from "styles/components/deviceAlarms.styles";
import {
  getErrorDetails,
  getAlarmDisplayType,
  getNotificationDetailsIcon,
  getAlarmSeverity,
} from "utils/deviceAlarmsData";
import { AlarmModalProps } from "types/deviceAlarms.types";

const AlarmModal: React.FC<AlarmModalProps> = ({
  visible,
  selectedAlarm,
  onHide,
  onMarkAsRead,
  isLoading,
}) => {
  if (!selectedAlarm) return null;

  const errorDetails = getErrorDetails(selectedAlarm.errorId);
  const alarmIcon = getNotificationDetailsIcon(selectedAlarm.errorId);
  const severity = getAlarmSeverity(selectedAlarm.type);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return isValid(date)
      ? format(date, "MMM dd, yyyy 'at' h:mm a")
      : "Invalid Date";
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: "600px" }}
      pt={{
        content: {
          style: {
            padding: "0",
            borderRadius: "8px",
            overflow: "hidden",
          },
        },
      }}
      showHeader={false}
    >
      <ModalContent>
        <ModalHeader severity={severity}>
          <ErrorIcon>
            <img src={alarmIcon} alt={errorDetails.type} />
          </ErrorIcon>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.5rem", fontWeight: "600" }}>
              {errorDetails.title}
            </h2>
            <p
              style={{ margin: "0.5rem 0 0 0", opacity: 0.9, fontSize: "1rem" }}
            >
              {errorDetails.message}
            </p>
          </div>
        </ModalHeader>

        <ModalBody>
          <AlarmInfo>
            <div className="info-item">
              <span className="label">Alarm ID</span>
              <span className="value">#{selectedAlarm.id}</span>
            </div>
            <div className="info-item">
              <span className="label">Type</span>
              <span className="value">
                {getAlarmDisplayType(selectedAlarm.errorId, selectedAlarm.type)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Value</span>
              <span className="value">{selectedAlarm.value}</span>
            </div>
            <div className="info-item">
              <span className="label">Occurred</span>
              <span className="value">
                {formatDate(selectedAlarm.createdAt)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Status</span>
              <span className="value">
                {selectedAlarm.isRead ? "Acknowledged" : "Unread"}
              </span>
            </div>
          </AlarmInfo>

          <TroubleshootingSection>
            <h3>Recommended Actions</h3>
            <TroubleshootingList>
              {errorDetails.troubleshooting.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </TroubleshootingList>
          </TroubleshootingSection>

          <ModalActions>
            <Button
              label="Close"
              icon="pi pi-times"
              onClick={onHide}
              severity="secondary"
              outlined
            />
            <Button
              disabled={isLoading || selectedAlarm.isRead}
              loading={isLoading}
              label="Mark as Resolved"
              icon="pi pi-check"
              onClick={() => onMarkAsRead(selectedAlarm.id)}
              severity="success"
            />
          </ModalActions>
        </ModalBody>
      </ModalContent>
    </Dialog>
  );
};

export default AlarmModal;
